import {
    Feature,
    FeatureCollection,
    LineString,
    lineString,
    MultiLineString,
    point,
    Point,
    Position,
} from "@turf/helpers";
import { envelope } from "@turf/turf";
import mapboxgl, { GeoJSONSource } from "mapbox-gl";
import { LaneDetails } from "models/laneDetails/LaneDetails";
import { LaneMapDetails } from "models/laneMapDetails/LaneMapDetails";
import { RouteDetails } from "models/routeDetails/RouteDetails";
import { StopDetails } from "models/routeDetails/StopDetails";
import { theme } from "styling/theme";

function isNumeric(value: any) {
    return (
        !isNaN(value as any) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
        !isNaN(parseFloat(value))
    ); // ...and ensure strings of whitespace fail
}

export function isValidCoordinates(point: [number | string, number | string]) {
    return isNumeric(point[0]) && isNumeric(point[1]);
}

export function isValidLaneCoordinates(lane: LaneDetails | LaneMapDetails) {
    const isValid =
        isValidCoordinates([lane.originLongitude, lane.originLatitude]) &&
        isValidCoordinates([lane.destLatitude, lane.destLongitude]);

    if (!isValid) {
        console.log(`Invalid coordinates for lane ${lane.laneId}`);
    }

    return isValid;
}

export function isValidRoute(route: RouteDetails) {
    let isValid = true;

    route.stopDetails.forEach(
        (x) =>
            (isValid = isValid && isValidCoordinates([x.longitude, x.latitude]))
    );

    if (route.stopDetails.length < 2) {
        isValid = true;
    }

    if (!isValid) {
        console.log(
            new Error(
                `Route ${route.routeName} is invalid, id = ${route.routeId}`
            )
        );
    }

    return isValid;
}

// TODO: use the same function here and for total widgets
export function riskLevelToColor(riskLevel: string) {
    switch (riskLevel.toLowerCase()) {
        case "healthy":
            return theme.colors.secondary.sky;
        case "low":
            return theme.colors.feedback.success;
        case "medium":
            return theme.colors.feedback.warning;
        case "high":
            return theme.colors.feedback.error;
        default:
            console.log(`riskLevelToColor: risk Level ${riskLevel} is invalid`);
            return "#000000";
    }
}

// function getAngleWithPoint1(point1: Position, point2: Position) {
//     return Math.atan2(point2[1] - point1[1], point2[0] - point1[0]);
// }

// function getMidPointWithPoint1(point1: Position, point2: Position) {
//     return [(point1[0] + point2[0]) / 2, (point1[1] + point2[1]) / 2];
// }

// function getControlPointWithPoint1(
//     point1: Position,
//     point2: Position,
//     length: number,
//     clockwise: boolean
// ) {
//     const angle = getAngleWithPoint1(point1, point2);
//     const direction = clockwise ? 1 : -1;
//     const perpendicularAngle = angle + (direction * Math.PI) / 2;
//     const midPoint = getMidPointWithPoint1(point1, point2);
//     return [
//         midPoint[0] + Math.cos(perpendicularAngle) * length,
//         midPoint[1] + Math.sin(perpendicularAngle) * length,
//     ];
// }

function getCoordinates(
    startLng: number,
    startLat: number,
    endLng: number,
    endLat: number
) {
    let correction = 0;

    // Address case when the shortest path goes over to the left/right world copy
    if (endLng - startLng > 180) {
        correction = -360;
    } else if (startLng - endLng > 180) {
        correction = 360;
    }

    return { start: [startLng, startLat], end: [endLng + correction, endLat] };
}

// function getBezierCurveSpline(
//     start: Position,
//     end: Position,
//     steps: number = 50
// ) {
//     const controlPoint = getControlPointWithPoint1(
//         start,
//         end,
//         distance(start, end) / 500,
//         start[0] < end[0]
//     );

//     const curve = new Bezier(
//         start[0],
//         start[1],
//         controlPoint[0],
//         controlPoint[1],
//         end[0],
//         end[1]
//     );

//     const spline = [];

//     for (let i = 0; i <= steps; i++) {
//         const { x, y } = curve.get(i / 50);
//         spline.push([x, y]);
//     }

//     return spline;
// }

function shipmentCountToWidth(count: number) {
    if (count <= 10) {
        return 2;
    } else if (count <= 50) {
        return 3;
    } else {
        return 4;
    }
}

export function convertLaneToLineString(lane: LaneDetails | LaneMapDetails) {
    const { start, end } = getCoordinates(
        parseFloat(lane.originLongitude),
        parseFloat(lane.originLatitude),
        parseFloat(lane.destLongitude),
        parseFloat(lane.destLatitude)
    );

    const properties = {
        color: riskLevelToColor(lane.riskLevel),
        width: shipmentCountToWidth(lane.shipmentCount),
    };

    const points = [start, end];

    return lineString(points, properties);
}

export function convertLaneToStraightShortestPath(
    lane: LaneDetails
): FeatureCollection<LineString> {
    const { start, end } = getCoordinates(
        parseFloat(lane.originLongitude),
        parseFloat(lane.originLatitude),
        parseFloat(lane.destLongitude),
        parseFloat(lane.destLatitude)
    );

    const properties = {
        color: riskLevelToColor(lane.riskLevel),
    };

    const geojson: FeatureCollection<LineString> = {
        type: "FeatureCollection",
        features: [],
    };

    if (!isValidLaneCoordinates(lane)) {
        return geojson;
    }

    const feature = lineString([start, end], properties);

    geojson.features.push(feature);

    return geojson;
}

export function convertLaneToPoints(lane: LaneMapDetails) {
    const origin = point(
        [parseFloat(lane.originLongitude), parseFloat(lane.originLatitude)],
        {
            fillColor: riskLevelToColor(lane.riskLevel),
            strokeColor: riskLevelToColor(lane.riskLevel),
        }
    );

    const destination = point(
        [parseFloat(lane.destLongitude), parseFloat(lane.destLatitude)],
        {
            fillColor: "#FFFFFF",
            strokeColor: riskLevelToColor(lane.riskLevel),
        }
    );

    return [origin, destination];
}

export function convertLanesToGeojson(
    lanes: LaneDetails[] | LaneMapDetails[]
): FeatureCollection<LineString> {
    const arcs = lanes
        .filter((lane) => isValidLaneCoordinates(lane))
        .map(convertLaneToLineString);

    return {
        type: "FeatureCollection",
        features: arcs,
    };
}

export function convertLanesToPoints(
    lanes: LaneDetails[] | LaneMapDetails[]
): FeatureCollection<Point> {
    const points = lanes
        .filter((lane) => isValidLaneCoordinates(lane))
        .map(convertLaneToPoints)
        .flat();

    return {
        type: "FeatureCollection",
        features: points,
    };
}

function convertStopDetailsToSegments(stops: StopDetails[]) {
    const segments: Position[][] = [];

    for (let i = 0; i < stops.length - 1; i++) {
        let [lng, lat, nextLng, nextLat] = [
            parseFloat(stops[i].longitude),
            parseFloat(stops[i].latitude),
            parseFloat(stops[i + 1].longitude),
            parseFloat(stops[i + 1].latitude),
        ];

        nextLng += nextLng - lng > 180 ? -360 : lng - nextLng > 180 ? 360 : 0;

        const segment: Position[] = [
            [lng, lat],
            [nextLng, nextLat],
        ];
        segments.push(segment);
    }

    return segments;
}

export function convertRouteToLineString(
    route: RouteDetails
): Feature<MultiLineString> {
    const feature: Feature<MultiLineString> = {
        type: "Feature",
        properties: {
            color: riskLevelToColor(route.riskLevel),
        },
        geometry: {
            type: "MultiLineString",
            coordinates: convertStopDetailsToSegments(route.stopDetails),
        },
    };

    return feature;
}

export function convertRoutesToGeojson(
    routes: RouteDetails[]
): FeatureCollection<MultiLineString> {
    const validRoutes = routes.filter((x) => isValidRoute(x));

    return {
        type: "FeatureCollection",
        features: validRoutes.map((x) => convertRouteToLineString(x)),
    };
}

export function setSourceData(
    geojson: any,
    sourceId: string,
    map?: mapboxgl.Map
) {
    if (map && map.getSource(sourceId)) {
        (map.getSource(sourceId) as GeoJSONSource).setData(geojson);
    }
}

export function fitBoundsToGeojson(
    geojson: FeatureCollection,
    map?: mapboxgl.Map
) {
    if (!map || geojson.features.length === 0) {
        return;
    }

    const enveloped = envelope(geojson);

    if (enveloped.bbox) {
        map.fitBounds(enveloped.bbox as [number, number, number, number], {
            padding: 50,
            speed: 3,
        });
    }
}
